import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, refreshSession, setAccessToken, getAccessToken } from './api';

/**
 * A role is no longer one of five known strings: an estate can define its own.
 * The client never decides what a role *means* — it reads `permissions`, which
 * the server resolves — so this stays a plain string on purpose.
 */
export type Role = string;

export type User = {
  id: string;
  email: string;
  name: string | null;
  role: Role | null;
  totpEnabled: boolean;
};

export type Tenant = {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  locale: string;
  units: 'metric' | 'imperial';
};

export type Branding = {
  productName?: string;
  logoUrl?: string;
  primaryColour?: string;
  supportEmail?: string;
  supportUrl?: string;
};

export type Org = {
  id: string;
  name: string;
  slug: string;
  status?: string;
  branding?: Branding;
};

/** Which hat the session is currently wearing. */
export type Scope = {
  kind: 'platform' | 'org' | 'tenant';
  orgId: string | null;
  tenantId: string | null;
  role: Role | null;
  platformRole: 'superadmin' | 'support' | null;
  viaOrg?: boolean;
  impersonated?: boolean;
  impersonationMode?: 'read' | 'write' | null;
};

/** Everything this person could switch into. */
export type AvailableScopes = {
  platform: { role: 'superadmin' | 'support' } | null;
  orgs: Array<{ id: string; name: string; slug: string; role: string }>;
  tenants: Array<{ id: string; name: string; slug: string; org_id: string; org_name: string; role: string; via: 'member' | 'org' }>;
};

type Session = {
  user: User;
  tenant: Tenant | null;
  org: Org | null;
  scope: Scope | null;
  scopes: AvailableScopes;
  permissions: string[];
};

type AuthValue = Session & {
  status: 'loading' | 'authenticated' | 'anonymous' | 'setup';
  can: (permission: string) => boolean;
  /** True while a platform operator is inside somebody's estate. */
  isImpersonating: boolean;
  login: (email: string, password: string, totp?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  switchScope: (target: { kind: Scope['kind']; orgId?: string; tenantId?: string }) => Promise<void>;
  adoptSession: (body: any) => void;
  exitImpersonation: () => Promise<void>;
};

/** Exported so tests can render a component under a chosen set of permissions. */
export const AuthContext = createContext<AuthValue | null>(null);

const NO_SCOPES: AvailableScopes = { platform: null, orgs: [], tenants: [] };

const EMPTY: Session = {
  user: null as unknown as User,
  tenant: null,
  org: null,
  scope: null,
  scopes: NO_SCOPES,
  permissions: [],
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>(EMPTY);
  const [status, setStatus] = useState<AuthValue['status']>('loading');

  const adopt = useCallback((body: any) => {
    // A scope switch and an impersonation both return a token; /me does not,
    // and must not clear the one already held.
    setAccessToken(body.accessToken ?? getAccessToken());
    setSession({
      user: body.user ?? EMPTY.user,
      tenant: body.tenant ?? null,
      org: body.org ?? null,
      scope: body.scope ?? null,
      scopes: body.scopes ?? NO_SCOPES,
      permissions: body.permissions ?? [],
    });
    setStatus('authenticated');
  }, []);

  // On load, try to exchange the refresh cookie for a session. This is what
  // makes "stay signed in" work without ever putting a token in storage.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Through the shared single-flight, never a bare POST: React re-runs
      // this effect on mount in development, and a second refresh with the
      // same rotating token reads as a replay and revokes the session.
      const body = await refreshSession();
      if (body) {
        if (!cancelled) adopt(body);
        return;
      }

      try {
        const { needsSetup } = await api.get('/api/auth/setup', { token: null });
        if (!cancelled) setStatus(needsSetup ? 'setup' : 'anonymous');
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();
    return () => { cancelled = true; };
  }, [adopt]);

  const login = useCallback(async (email: string, password: string, totp?: string) => {
    const body = await api.post('/api/auth/login', { email, password, ...(totp ? { totp } : {}) }, { token: null });
    adopt(body);
  }, [adopt]);

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout').catch(() => {});
    setAccessToken(null);
    setSession(EMPTY);
    setStatus('anonymous');
  }, []);

  const refresh = useCallback(async () => {
    const body = await api.get('/api/auth/me');
    setSession((prev) => ({
      ...prev,
      user: body.user ?? prev.user,
      tenant: body.tenant ?? null,
      org: body.org ?? null,
      scope: body.scope ?? null,
      scopes: body.scopes ?? NO_SCOPES,
      permissions: body.permissions ?? [],
    }));
  }, []);

  const switchScope = useCallback(async (target: { kind: Scope['kind']; orgId?: string; tenantId?: string }) => {
    const body = await api.post('/api/auth/scope', target);
    adopt(body);
  }, [adopt]);

  const exitImpersonation = useCallback(async () => {
    const body = await api.post('/api/platform/session/exit', {});
    adopt(body);
  }, [adopt]);

  const value = useMemo<AuthValue>(() => ({
    ...session,
    status,
    can: (permission: string) => session.permissions.includes(permission),
    isImpersonating: !!session.scope?.impersonated,
    login,
    logout,
    refresh,
    switchScope,
    adoptSession: adopt,
    exitImpersonation,
  }), [session, status, login, logout, refresh, switchScope, adopt, exitImpersonation]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}

/** Convenience for hiding actions the signed-in role cannot perform. */
export function useCan(permission: string): boolean {
  return useAuth().can(permission);
}
